import { useLife } from '@/state/life';
import { usePower } from '@/state/power';
import { useXClone } from '@/state/xclone';
import { usePhone } from '@/state/phone';
import { useNotifications } from '@/state/notifications';
import { useCinematic } from '@/components/CinematicTakeover';
import { useMeta } from '@/state/meta';
import { sounds } from '@/audio/synth';
import { addImpulse } from './prices';
import { PHONE_ACTION_HANDLERS } from './phoneActions';

/**
 * Fed Decision arc — JPow gives a presser, the market moves wildly.
 *
 * Triggered as a recurring "Fed Day" event from random-events. 4 beats:
 *  1. The Statement (you guess the tone)
 *  2. The Q&A (you read JPow's body language)
 *  3. The Pivot (you decide to fade or follow)
 *  4. The Aftermath (a CNBC hot-take is filed)
 *
 * Real impulses fire on most tickers. Player can position via the chart
 * before each beat resolves.
 */

interface Beat {
  text: string;
  options: { label: string; effect: () => void }[];
}

const BEATS: Beat[] = [
  {
    text: 'JPOW STATEMENT just dropped: "...committee remains data-dependent..." what does this mean? you have 14 seconds.',
    options: [
      { label: '"hawkish" — sell everything', effect: () => {
        addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 6 * 60_000, logDelta: -0.04 });
        addImpulse({ ticker: 'TARI', startMs: Date.now(), durationMs: 6 * 60_000, logDelta: -0.03 });
        addImpulse({ ticker: 'CHIP', startMs: Date.now(), durationMs: 6 * 60_000, logDelta: -0.05 });
      } },
      { label: '"dovish" — buy everything', effect: () => {
        addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 6 * 60_000, logDelta: 0.05 });
        addImpulse({ ticker: 'TARI', startMs: Date.now(), durationMs: 6 * 60_000, logDelta: 0.04 });
        addImpulse({ ticker: 'CHIP', startMs: Date.now(), durationMs: 6 * 60_000, logDelta: 0.07 });
      } },
      { label: '"neutral" — fade everyone else\'s reaction', effect: () => {
        useLife.getState().bumpSanity(2);
      } },
    ],
  },
  {
    text: 'JPOW Q&A: "...risks remain balanced..." he\'s sweating slightly. how do you read it?',
    options: [
      { label: 'panic — actual rate cut incoming', effect: () => {
        addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 8 * 60_000, logDelta: 0.10 });
        addImpulse({ ticker: 'BTCO', startMs: Date.now(), durationMs: 8 * 60_000, logDelta: 0.08 });
      } },
      { label: 'no panic — just a guy', effect: () => {
        addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 8 * 60_000, logDelta: -0.04 });
      } },
      { label: 'order pizza, ignore the presser', effect: () => {
        useLife.getState().bumpSanity(4);
      } },
    ],
  },
  {
    text: 'POWELL PIVOT moment: "...the committee is open to all options..." crypto pumping. tech pumping. what do you do?',
    options: [
      { label: 'lever up. crypto and tech.', effect: () => {
        addImpulse({ ticker: 'BTCO', startMs: Date.now(), durationMs: 10 * 60_000, logDelta: 0.12 });
        addImpulse({ ticker: 'CHIP', startMs: Date.now(), durationMs: 10 * 60_000, logDelta: 0.10 });
        addImpulse({ ticker: 'OAEN', startMs: Date.now(), durationMs: 10 * 60_000, logDelta: 0.08 });
        useLife.getState().bumpSecHeat(4);
      } },
      { label: 'short the rip.', effect: () => {
        addImpulse({ ticker: 'BTCO', startMs: Date.now(), durationMs: 10 * 60_000, logDelta: -0.06 });
        addImpulse({ ticker: 'CHIP', startMs: Date.now(), durationMs: 10 * 60_000, logDelta: -0.04 });
      } },
      { label: 'sit it out. you\'ve been wrong about JPow before.', effect: () => {
        useLife.getState().bumpSanity(3);
      } },
    ],
  },
  {
    text: 'CNBC HOT-TAKE just filed: "the most consequential statement in 14 years." also: "no big deal." both panel members say both things.',
    options: [
      { label: 'tweet a hot take of your own', effect: () => {
        useXClone.getState().composePost("today\'s Fed move is — to be clear — historically unprecedented.");
        usePower.getState().bumpRep('fintwit', 6);
      } },
      { label: 'tell your followers you "called it"', effect: () => {
        useXClone.getState().composePost("called it.");
        usePower.getState().bumpRep('wsb', 8);
      } },
      { label: 'log off. the markets close in 14 minutes.', effect: () => {
        useLife.getState().bumpSanity(4);
      } },
    ],
  },
];

export function startFedDecisionArc(): void {
  useMeta.getState().recordLore('arc-fed-decision', 'Fed Day');
  useCinematic.getState().fire({
    id: `fed-day-${Date.now()}`,
    type: 'breaking',
    headline: 'FED DAY — JPOW LIVE.',
    subhead: 'rate decision in 4 minutes. statement in 8. presser in 30. tape will move 4-6% intraday on words you cannot translate.',
    durationMs: 5500,
  });
  sounds.notifyAlert();

  for (let i = 0; i < BEATS.length; i++) {
    const b = BEATS[i]!;
    setTimeout(() => {
      const ids = ['a', 'b', 'c'];
      const actions = b.options.map((opt, idx) => ({
        id: ids[idx]!,
        label: opt.label,
        effectId: `fed-q${i}-${ids[idx]}`,
      }));
      for (let j = 0; j < b.options.length; j++) {
        const id = `fed-q${i}-${ids[j]}`;
        const opt = b.options[j]!;
        PHONE_ACTION_HANDLERS[id] = () => {
          opt.effect();
          sounds.fill();
        };
      }
      usePhone.getState().receiveMessage({
        threadId: 'broker',
        fromNpc: 'broker',
        text: b.text,
        actions,
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: `fed: beat ${i + 1}/4` });
      sounds.notifyAlert();
    }, 5_000 + i * 25_000);
  }

  setTimeout(() => {
    useXClone.getState().receivePost({
      authorHandle: 'CNBCSquawk', authorTribe: 'cnbc',
      text: 'POST-FED: powell\'s statement was both \"hawkish\" and \"dovish\" depending on which 12-second clip you watch. analysts have not converged. tape has. tape is fine.',
      likes: 12000, reposts: 3400,
    });
  }, 5_000 + BEATS.length * 25_000 + 5_000);
}
